import type * as schemas from "@common/schemas/v2";
import { FillerStep } from "./filler";
import { UserClickStep } from "./userClick";
import { DragStep } from "./drag";
import { HighlightStep } from "./highlight";
import { QuestionFactory, QuestionStep } from "./question";
import { ShowContentStep } from "./showContent";
import { ModalFactory, ModalStep } from "./modal";
import { WaitForElementStep } from "./waitForElement";
import { AutoTypeStep } from "./autoType";
import { AutoClickStep } from "./autoClick";
import { SleepStep } from "./sleep";
import { UserInputStep } from "./userInput";
import { ExtractMethods } from "@/lib/typeUtils";
import { StepActionsFacade } from "../../tourPlayback/facades";
import { RedirectStep } from "./redirect";
import { FlowFlexibility } from "@common/schemas";

export type Step =
  | FillerStep
  | UserClickStep
  | DragStep
  | HighlightStep
  | QuestionStep
  | ShowContentStep
  | ModalStep
  | WaitForElementStep
  | AutoTypeStep
  | AutoClickStep
  | SleepStep
  | RedirectStep
  | UserInputStep;

export type StepActions = ExtractMethods<StepActionsFacade>;

export interface StepFactoryParams {
  actions: StepActions;
  flowFlexibility: FlowFlexibility;
}

export class StepFactory {
  static create({ step, actions, flowFlexibility }: { index: number; step: schemas.Step } & StepFactoryParams): Step {
    switch (step.attributes.type) {
      case "filler":
        return new FillerStep(step, actions, flowFlexibility);
      case "userClick":
        return new UserClickStep(step, actions, flowFlexibility);
      case "drag":
        return new DragStep(step, actions, flowFlexibility);
      case "highlight":
        return new HighlightStep(step, actions, flowFlexibility);
      case "question":
        return QuestionFactory.create(step as schemas.QuestionStep, actions, flowFlexibility);
      case "redirect":
        return new RedirectStep(step, actions, flowFlexibility);
      case "modal":
        return ModalFactory.create(step as schemas.ModalStep, actions, flowFlexibility);
      case "showContent":
        return new ShowContentStep(step, actions, flowFlexibility);
      case "waitForElement":
        return new WaitForElementStep(step, actions, flowFlexibility);
      case "autoType":
        return new AutoTypeStep(step, actions, flowFlexibility);
      case "autoClick":
        return new AutoClickStep(step, actions, flowFlexibility);
      case "sleep":
        return new SleepStep(step, actions, flowFlexibility);
      case "userInput":
        return new UserInputStep(step, actions, flowFlexibility);
      default:
        throw new Error(`Unknown step type: ${(step as schemas.Step).attributes.type}`);
    }
  }

  static createList({ steps, actions, flowFlexibility }: { steps: schemas.Step[] } & StepFactoryParams): Step[] {
    if (!steps) {
      return [];
    }

    return steps.map((step, index) =>
      StepFactory.create({
        step,
        index,
        actions,
        flowFlexibility,
      })
    );
  }
}
